import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import {
  SHOW_VIDEO_PLAYER_END_SCREEN,
  SIGN_UP_CTA_CLICK,
  WATCH_AGAIN_CLICK,
} from 'constants/events';
import { GREETINGS_PAGE, SIGNUP_PAGE } from 'constants/routes';
import * as analytics from 'utilities/analytics';
import { isInstalled } from 'utilities/extension';

import { useFlagIsActivated } from '../../../hooks/featureFlag';
import {
  GROW_END_OF_VIDEO_REPLY_CTA,
  ROLLOUT_WEBSITE_RECORDING,
} from '../../../../../shared/constants/featureFlag';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import CustomCtaButton from 'components/share-video/cta-button';
import Button from 'components/ui-components/button';

import './styles.less';

const WRURecordingButton = React.lazy(() =>
  import('../../web-recording-utility/wru-recording-button')
);

const VideoReplyButton = ({ onWatchAgainClick }) => {
  return (
    <FeatureFlaggedComponent
      flag={ROLLOUT_WEBSITE_RECORDING}
      activationValue={true}
    >
      <React.Suspense fallback={<div>Loading...</div>}>
        <div className="end-screen-record-video-reply-button">
          <WRURecordingButton
            isVideoReply={true}
            label="Record a Reply"
            buttonSize="large"
            menuPosition="bottom"
            location="end-screen"
          />
          <WatchAgainCta onWatchAgainClick={onWatchAgainClick} />
        </div>
      </React.Suspense>
    </FeatureFlaggedComponent>
  );
};

const VideoReplyLargeCta = ({ onWatchAgainClick, onCTAClick, videoId }) => {
  useEffect(() => {
    analytics.track(SHOW_VIDEO_PLAYER_END_SCREEN, {
      cta_type: 'large-cta',
      video_id: videoId,
      from_url: window.location.href,
    });
  }, [videoId]);

  return (
    <div className="install">
      <FeatureFlaggedComponent
        flag={GROW_END_OF_VIDEO_REPLY_CTA}
        activationValue="show-old"
      >
        <h3 className="static-cta">Get Loom to become faster than typing</h3>
      </FeatureFlaggedComponent>
      <FeatureFlaggedComponent
        flag={GROW_END_OF_VIDEO_REPLY_CTA}
        activationValue="show-new"
      >
        <h3 className="static-cta">Reply now with a Loom of your own</h3>
      </FeatureFlaggedComponent>

      <Button
        buttonType="rp-primary-2"
        value="Get Loom For Free"
        onClicked={onCTAClick}
      />
      <a target="_blank" href="/">
        go to loom.com
      </a>
      <WatchAgainCta onWatchAgainClick={onWatchAgainClick} />
    </div>
  );
};

const LoomGreetingsCta = ({ onWatchAgainClick, onCTAClick, videoId }) => {
  useEffect(() => {
    analytics.track(SHOW_VIDEO_PLAYER_END_SCREEN, {
      cta_type: 'loom-greetings-cta',
      video_id: videoId,
      from_url: window.location.href,
    });
  }, [videoId]);

  return (
    <div className={classNames('video-player-end-screen', 'large-cta')}>
      <div className="install">
        <h3 className="static-cta">Send a message just like this one</h3>
        <Button
          buttonType="rp-primary-2"
          value="Loom Greetings"
          onClicked={onCTAClick}
        />
        <a target="_blank" href={GREETINGS_PAGE} rel="noopener noreferrer">
          go to loom.com/greetings
        </a>
        <WatchAgainCta onWatchAgainClick={onWatchAgainClick} />
      </div>
    </div>
  );
};

const WatchAgainCta = ({ onWatchAgainClick }) => (
  <div className="watch-again-cta" onClick={onWatchAgainClick}>
    <div className="watch-again-icon" />
    <div>Watch Again</div>
  </div>
);

const EndScreen = props => {
  const {
    ctaEnabled,
    isLoggedIn,
    commentsEnabled,
    loomBrandedPlayer,
    isMainVideoOnSharePage,
    isLoomGreeting,
    isInlineEmbedOnLoom,
    videoId,
    fromPublicSharePage,
  } = props;

  const [hasCheckedIfInstalled, setHasCheckedIfInstalled] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    setHasCheckedIfInstalled(true);

    const checkInstallation = async () => {
      setInstalled(await new Promise(resolve => isInstalled(resolve)));
    };

    checkInstallation();
  }, []);

  const showCtaExperiment = useFlagIsActivated({
    flag: ROLLOUT_WEBSITE_RECORDING,
    activationValue: false,
  });

  const onWatchAgainClick = ev => {
    ev.stopPropagation();

    analytics.track(WATCH_AGAIN_CLICK, { video_id: videoId });

    props.onRequestCurrentTime(0);
    props.onRequestPlay();
  };

  const onCTAClick = ev => {
    ev.stopPropagation();

    analytics.track(SIGN_UP_CTA_CLICK, {
      source: 'End of video',
      from_public_share_page: fromPublicSharePage,
      is_inline_embed_on_loom: isInlineEmbedOnLoom,
      video_id: videoId,
      isLoomGreeting,
      from_url: window.location.href,
    });

    window.open(isLoomGreeting ? GREETINGS_PAGE : SIGNUP_PAGE, '_blank');
  };

  if (isLoomGreeting) {
    return (
      <LoomGreetingsCta
        onWatchAgainClick={onWatchAgainClick}
        onCTAClick={onCTAClick}
        videoId={videoId}
      />
    );
  }

  // if logged in and installed, don't upsell
  const showLargeCTA = !installed && !isLoggedIn && loomBrandedPlayer;

  if (!hasCheckedIfInstalled) {
    return null;
  }

  const showVideoReply = isMainVideoOnSharePage && commentsEnabled;

  return (
    <div
      className={classNames(
        'video-player-end-screen',
        ctaEnabled && 'has-custom-cta',
        showLargeCTA && showCtaExperiment && 'large-cta'
      )}
    >
      {!ctaEnabled && showVideoReply && (
        <VideoReplyButton onWatchAgainClick={onWatchAgainClick} />
      )}
      {ctaEnabled && <CustomCtaButton onClick={props.onCustomCTAClick} />}
      {(ctaEnabled ||
        (!ctaEnabled && !showVideoReply && !showCtaExperiment)) && (
        <WatchAgainCta onWatchAgainClick={onWatchAgainClick} />
      )}
      {showCtaExperiment && (
        <div>
          {!showLargeCTA && !ctaEnabled && (
            <WatchAgainCta onWatchAgainClick={onWatchAgainClick} />
          )}
          {!ctaEnabled && showLargeCTA && !isLoomGreeting && (
            <VideoReplyLargeCta
              onWatchAgainClick={onWatchAgainClick}
              onCTAClick={onCTAClick}
              videoId={videoId}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default EndScreen;
